import "../TableComponent/table.scss";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import NoDataIcon from "../../../assets/icons/NoDataFound";
import {
    ArrowUp,
    ChevronArrowUp,
    ArrowDown,
    ChevronArrowDown,
    ChevronArrowRight,
} from "../../../assets/icons";
import WidgetLoader from "../widgetloader";
import { useAppSelector } from "../../../store/hooks/StoreHooks";

interface TableColumn {
    key: string;
    render?: any;
    title: string;
    width?: string;
    dataIndex: string;
    sortable?: boolean;
    minwidth?: string;
}

interface TableProps {
    name?: string;
    data: { [key: string]: boolean | number | string | undefined | null | any }[];
    height?: number;
    rowsPerPage: number;
    emptyIcon?: boolean;
    tableClass?: string;
    errorMessage?: string;
    columns: TableColumn[];
    initialSortField?: string;
    initialSortOrder?: string;
    handleSortChange?: (sortOrder: string, sortBy: string) => void;
    overFlowClass?: string;
    isLoading?: boolean;
    minimumHeight?: number;
    hasMore?: boolean;
    totalCount?: number;
    rowHeightPx?: number;
    maxTableHeightPx?: number;
    onLoadMore?: () => void;
    renderExpandedRow?: (rowData: {
        [key: string]: boolean | number | string | undefined | null | any;
    }, rowIndex?: number) => JSX.Element;
    clickableRows?: boolean;
    onRowClick?: (row: any) => void;
    singleExpandMode?: boolean;
}

interface SortState {
    [key: string]: "none" | "ASC" | "DESC";
}

const InfiniteScrollTable = forwardRef<HTMLElement, TableProps>(
    (
        {
            name,
            data = [],
            columns,
            tableClass,
            height = 500,
            errorMessage,
            emptyIcon = true,
            handleSortChange,
            initialSortField,
            initialSortOrder,
            overFlowClass,
            isLoading = false,
            minimumHeight,
            hasMore: hasMoreProp,
            totalCount,
            rowHeightPx = 45,
            maxTableHeightPx = 1200,
            onLoadMore,
            renderExpandedRow,
            clickableRows,
            onRowClick,
            singleExpandMode,
        }, ref) => {
        const defaultWidth = `${100 / columns.length}%`;
        const [sortState, setSortState] = useState<SortState>({});
        const [isInitialized, setIsInitialized] = useState<boolean>(false);
        const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
        const [singleExpandedRow, setSingleExpandedRow] = useState<number | null>(null);
        const { customerEnabled, customerCreated } = useAppSelector((state) => state.auth);
        const { projectReadOnly } = useAppSelector((state) => state.projectHeader);
        const scrollContainerRef = useRef<HTMLDivElement>(null);
        const prevScrollTopRef = useRef<number>(0);
        const [hasCalledLoadMore, setHasCalledLoadMore] = useState(false);
        const prevDataLengthRef = useRef<number>(0);
        const userHasScrolledToBottomRef = useRef<boolean>(false);

        const dataLength = data?.length ?? 0;
        const hasMore = hasMoreProp ?? (totalCount != null ? dataLength < totalCount : true);
        const useDynamicHeight = !!onLoadMore;
        const effectiveHeight = useDynamicHeight
            ? Math.min(Math.max(dataLength * rowHeightPx, minimumHeight ?? 300), maxTableHeightPx)
            : (height ?? 500);

        useEffect(() => {
            if (data.length > 10 && scrollContainerRef.current) {
                const table = scrollContainerRef.current.querySelector("table");
                if (table) {
                    const rows = table.querySelectorAll("tbody tr");
                    const targetRow = rows[data.length - 14];
                    if (targetRow && targetRow instanceof HTMLElement) {
                        const container = scrollContainerRef.current;
                        const rowTop = targetRow.offsetTop;
                        container.scrollTop = rowTop;
                    }
                }
            }
        }, [data.length]);

        useEffect(() => {
            if (Object.keys(columns).length > 0 && !isInitialized) {
                const initialSortState = columns.reduce((acc, column) => {
                    if (column.sortable) {
                        acc[column.dataIndex] = column.dataIndex === initialSortField && initialSortOrder
                            ? (initialSortOrder === "ASC" ? "ASC" : "DESC") : "none";
                    }
                    return acc;
                }, {} as SortState);

                setSortState(initialSortState);
                setIsInitialized(true);

                if (initialSortField && initialSortOrder) {
                    handleSortChange?.(initialSortOrder, initialSortField);
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [columns, initialSortField, initialSortOrder]);

        const scrollThresholdPx = 10;

        useEffect(() => {
            if (dataLength > prevDataLengthRef.current) {
                prevDataLengthRef.current = dataLength;
                setHasCalledLoadMore(false);
            }
        }, [dataLength]);

        useEffect(() => {
            const container = scrollContainerRef.current;
            if (!container || !onLoadMore || !hasMore) return;

            const handleScroll = () => {
                const { scrollTop, scrollHeight, clientHeight } = container;
                if (scrollTop !== prevScrollTopRef.current) {
                    prevScrollTopRef.current = scrollTop;
                    if (scrollTop + clientHeight >= scrollHeight - 100) {
                        if (!hasCalledLoadMore) {
                            setHasCalledLoadMore(true);
                            onLoadMore();
                        }
                    } else {
                        setHasCalledLoadMore(false);
                    }
                }
                const atBottom = scrollTop + clientHeight >= scrollHeight - scrollThresholdPx;
                if (atBottom) {
                    setHasCalledLoadMore(true);
                }
            };

            container.addEventListener("scroll", handleScroll);
            return () => container.removeEventListener("scroll", handleScroll);
        }, [onLoadMore, hasCalledLoadMore, hasMore]);

        useEffect(() => {
            const container = scrollContainerRef.current;
            if (!container || !onLoadMore || !hasMore || isLoading || hasCalledLoadMore) return;
            if (!userHasScrolledToBottomRef.current) return;

        }, [dataLength, hasMore, isLoading, onLoadMore, hasCalledLoadMore]);

        const toggleSortOrder = (column: string) => {
            setSortState((prevState) => {
                let newOrder: "none" | "ASC" | "DESC" = "none";
                if (prevState[column] === "none") newOrder = "ASC";
                else if (prevState[column] === "ASC") newOrder = "DESC";
                else if (prevState[column] === "DESC") newOrder = "none";

                const updatedState: SortState = {
                    ...prevState,
                    [column]: newOrder,
                };

                Object.keys(updatedState).forEach((key) => {
                    if (key !== column) updatedState[key] = "none";
                });
                handleSortChange && handleSortChange(newOrder, column);
                return updatedState;
            });
        };

        const toggleRowExpansion = (rowIndex: number) => {
            if (singleExpandMode) {
                setSingleExpandedRow((prevIndex) => (prevIndex === rowIndex ? null : rowIndex));
            } else {
                setExpandedRows((prevExpandedRows) => {
                    const updatedRows = new Set(prevExpandedRows);
                    if (updatedRows.has(rowIndex)) {
                        updatedRows.delete(rowIndex);
                    } else {
                        updatedRows.add(rowIndex);
                    }
                    return updatedRows;
                });
            }
        };

        const handleRowClickInternal = (row: any, rowIndex: number) => {
            if (clickableRows && onRowClick) {
                onRowClick(row);
            }
        };

        const renderTableHeader = () => (
            <thead style={{ zIndex: 992 }}>
                <tr>
                    {renderExpandedRow && <th></th>}
                    {columns.map((column) => (
                        <th
                            key={column.key}
                            className={`px-[12px] py-[16px] text-[12px] cursor-pointer ${column.sortable && sortState[column.dataIndex] !== "none"
                                ? "text-primary-400 py-[12px] px-[16px]"
                                : "text-neutral-25"
                                } hover:bg-darkcolor-500`}
                            style={{ width: column.width }}
                            onClick={() => column.sortable && toggleSortOrder(column.dataIndex)}
                        >
                            <div className={`flex items-center ${column.sortable && sortState[column.dataIndex] !== "none"
                                ? "table-header-shadow"
                                : " "
                                }`}>
                                <span className={`text-[12px] font-semibold ${column.dataIndex}`}>
                                    {column.title}
                                </span>
                                {column.sortable && (
                                    <div className="ml-1 flex flex-col items-center">
                                        {sortState[column.dataIndex] === "ASC" && (
                                            <ArrowDown
                                                color={`${column.sortable &&
                                                    sortState[column.dataIndex] !== "none"
                                                    ? "#60BFDA"
                                                    : "#F7F9F9"
                                                    }`}
                                            />
                                        )}
                                        {sortState[column.dataIndex] === "DESC" && (
                                            <ArrowUp
                                                color={`${column.sortable &&
                                                    sortState[column.dataIndex] !== "none"
                                                    ? "#60BFDA"
                                                    : "#F7F9F9"
                                                    }`}
                                            />
                                        )}
                                        {sortState[column.dataIndex] === "none" && (
                                            <>
                                                <ChevronArrowUp
                                                    size={15}
                                                    className="opacity-50 -mb-[10px]"
                                                />
                                                <ChevronArrowDown size={15} className="opacity-50" />
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </th>
                    ))}
                </tr>
            </thead>
        );

        const renderTableBody = () => (
            <tbody>
                {isLoading ? (
                    <tr>
                        <td>
                            <div className="absolute inset-0 flex justify-center items-center bg-darkcolor-700 bg-opacity-50">
                                <WidgetLoader isLoading={isLoading} />
                            </div>
                        </td>
                    </tr>
                ) : data.length > 0 ? (
                    data.map((row, rowIndex) => {
                        return (
                            <React.Fragment key={rowIndex}>
                                <tr
                                    onClick={() => {
                                        if ((!customerEnabled && !projectReadOnly) || (customerEnabled && customerCreated && !projectReadOnly)) {
                                            if (clickableRows) {
                                                handleRowClickInternal(row, rowIndex);
                                            }
                                            toggleRowExpansion(rowIndex);
                                        }
                                    }}
                                    className={`${clickableRows ? "cursor-pointer" : ""}`}
                                >
                                    {renderExpandedRow && (
                                        <td
                                            className='px-[24px] py-[9px]'
                                            style={{ width: "1%" }}
                                        >
                                            <ChevronArrowRight
                                                size={20}
                                                className={`${(!customerEnabled && !projectReadOnly) || (customerEnabled && customerCreated) ? "animate-pulse text-neutral-25 transition-transform duration-300 ease-in-out cursor-pointer" : "cursor-not-allowed opacity-35"} ${singleExpandMode
                                                    ? singleExpandedRow === rowIndex
                                                        ? "rotate-90"
                                                        : ""
                                                    : expandedRows.has(rowIndex)
                                                        ? "rotate-90"
                                                        : ""}`}
                                            />
                                        </td>
                                    )}
                                    {columns.map((column) => (
                                        <td
                                            key={column.key}
                                            className='px-[12px] py-[9px] text-neutral-25 text-[14px] hover:bg-darkcolor-500'
                                            style={{ width: column.width || defaultWidth, minWidth: column?.minwidth }}
                                        >
                                            {column.render
                                                ? column.render(row[column.key], row, rowIndex)
                                                : typeof row[column.key] === "boolean"
                                                    ? String(row[column.key])
                                                    : row[column.key]}
                                        </td>
                                    ))}
                                </tr>
                                {(singleExpandMode
                                    ? singleExpandedRow === rowIndex
                                    : expandedRows.has(rowIndex)) &&
                                    renderExpandedRow && (
                                        <tr>
                                            <td
                                                colSpan={columns.length + 1}
                                                className='px-[24px] py-[9px]'
                                            >
                                                {renderExpandedRow(row, rowIndex)}
                                            </td>
                                        </tr>
                                    )}
                            </React.Fragment>
                        );
                    })
                ) : (
                    <tr>
                        <td
                            colSpan={columns.length}
                            className="py-10"
                        >
                            <div className="flex flex-col items-center">
                                {emptyIcon && (
                                    <div className="text-neutral-25 mb-4">
                                        <NoDataIcon />
                                    </div>
                                )}
                                <span className="text-neutral-25 title">
                                    {errorMessage ?? "No data found"}
                                </span>
                            </div>
                        </td>
                    </tr>
                )}
            </tbody>
        );

        const scrollThreshold = 80;

        return (
            <>
                <div
                    ref={scrollContainerRef}
                    className={`scrollable relative overflow-y-auto ${overFlowClass}`}
                    style={{ minHeight: minimumHeight, maxHeight: effectiveHeight }}
                >
                    <table
                        className={`min-w-full rounded-[8px] outline-[1px] table over ${tableClass ? tableClass : ""
                            }`}
                        style={{ zIndex: 991 }}
                    >
                        {renderTableHeader()}
                        {renderTableBody()}
                    </table>
                    {onLoadMore && hasMore && (
                        <div style={{ height: scrollThreshold, minHeight: scrollThreshold }} aria-hidden="true" />
                    )}
                </div>
            </>
        );
    });

export default React.memo(InfiniteScrollTable);
